import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { PlatformStats } from '@avexa/api'
import { getAvexaClient } from '../../../lib/avexa'

/**
 * The homepage's four real numbers, from `public.platform_stats()`
 * (`supabase/migrations/20260922200000_dashboard_and_public_stats.sql`).
 *
 * ─── WHY AN RPC AND NOT A COUNT ─────────────────────────────────────────────
 *
 * CLAUDE.md §7, the entry this project has paid for more than any other: a
 * COUNT from a client is a lie when RLS is involved. RLS filters, so an
 * anonymous visitor asking `businesses` for `count: 'exact'` is told how many
 * rows THAT VISITOR may see — and gets HTTP 200 either way, so the number
 * renders as fact. `platform_stats()` is `security definer` and re-imposes the
 * predicate it bypasses, which is why the whole strip is one RPC rather than
 * three selects.
 *
 * ─── AND WHY THE QUERY ITSELF LIVES IN `@avexa/api` ─────────────────────────
 *
 * This hook called `db.rpc('platform_stats')` directly until 22 Sep. That is the
 * rule `@avexa/api` exists to enforce (Docs/03): one named, typed function per
 * query, so there is one place a policy is reasoned about and no way for the
 * website and the mobile app to drift on what a number means.
 *
 * It also fixed a type hole. `database.generated.ts` used to declare
 * `rating_average: number`, because PostgreSQL records no nullability for a
 * `returns table (…)` column and the generator had nowhere to read it from — so
 * the one field that is genuinely null when nothing has been reviewed was typed
 * as though it never could be. The generator now emits every such column
 * nullable, and `getPlatformStats` narrows the three that the SQL guarantees
 * while leaving this one alone. See `requireRpcColumns`.
 */

export type { PlatformStats }

/**
 * Five minutes, matching `CATEGORY_STALE_TIME`. These totals move a handful of
 * times a day at most, and the visitor feels the refetch on every route change
 * far more than they would ever feel a five-minute-old count.
 */
export const PLATFORM_STATS_STALE_TIME = 5 * 60 * 1000

/**
 * A LOCAL query key, unlike the category tree's.
 *
 * `useCategories` warns against inventing one, because the admin panel edits
 * the taxonomy and has to be able to invalidate it by the shared `qk` prefix.
 * Nothing edits these totals: they are a read-only aggregate of rows whose own
 * caches live elsewhere, so there is nothing for a mutation to invalidate and
 * no `qk` namespace to belong to.
 */
export const PLATFORM_STATS_KEY = ['platform', 'stats'] as const

export const usePlatformStats = (): UseQueryResult<PlatformStats> =>
  useQuery({
    queryKey: PLATFORM_STATS_KEY,
    queryFn: async (): Promise<PlatformStats> => {
      const { getPlatformStats } = await import('@avexa/api')
      const stats = await getPlatformStats(await getAvexaClient())

      // An empty result is a broken deployment, not "zero businesses" — the
      // aggregate always produces exactly one row — so this throws and the
      // section hides itself rather than rendering four confident zeroes.
      if (!stats) throw new Error('platform_stats() returned no row')
      return stats
    },
    staleTime: PLATFORM_STATS_STALE_TIME,
  })
